import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { Loader2, FileText, User, Upload, FolderOpen, Edit, Clock } from "lucide-react";
import { useActivityLogQuery } from "@/hooks/queries/useActivityLogQuery";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatAuditTimestamp } from "@/lib/dateUtils";

interface ActivityLogProps {
  projectId: number;
  maxHeight?: string;
}

const getIcon = (entityType: string, action: string) => {
  if (entityType === "File") return <Upload size={14} className="text-blue-500" />;
  if (entityType === "Folder") return <FolderOpen size={14} className="text-yellow-500" />;
  if (entityType === "ProjectUser") return <User size={14} className="text-cyan-500" />;
  if (entityType === "Project") return <Edit size={14} className="text-gray-500" />;
  return <FileText size={14} className="text-muted-foreground" />;
};

const ActivityLog = ({ projectId, maxHeight = "400px" }: ActivityLogProps) => {
  const { language } = useLanguage();
  const tz = useUserTimezone();
  const { t } = useTranslation(language);
  const { data: entries = [], isLoading } = useActivityLogQuery(projectId);

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t("justNow");
    if (diffInHours < 24) return `${diffInHours}h ${t("ago")}`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ${t("ago")}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock size={16} />
          {t("activityLog")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 size={18} className="animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t("noResults")}</p>
        ) : (
          <div className="overflow-y-auto pr-2" style={{ maxHeight }}>
            <div className="space-y-3">
              {entries.map((entry) => (
                <div key={entry.id} className="flex gap-3 items-start">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    {getIcon(entry.entityType, entry.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium break-words">{entry.description}</p>
                    <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{entry.actorName}</span>
                      <span>•</span>
                      <span
                        title={formatAuditTimestamp(entry.createdAt, tz)}
                        className="cursor-help"
                      >
                        {formatTimeAgo(entry.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityLog;

